import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { isProductConfigurable } from '@/util/isProductConfigurable';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getSortedColor } from './get-sorted-color';
import type { ConfigurableOption, Value } from './types/product';

export interface options_available_for_selection {
  __typename: string;
  option_value_uids: string[];
  attribute_code: string;
}

export const StandardOptions = ({
  configurableOptions,
  variants,
  product,
  handleClick,
  optionSpecs,
  handleColorLabelChange,
  classes,
}: {
  configurableOptions: ConfigurableOption[];
  variants: any[];
  product: any;
  handleClick: any;
  optionSpecs: string[];
  handleColorLabelChange: any;
  classes: any;
}) => {
  const form = useFormContext();
  const { watch } = form;
  const allFormData = watch();

  const variantStockStatusMap = useMemo(() => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return {};
    }

    return variants.reduce<Record<string, 0 | 1>>((acc, cur) => {
      const isInStock = cur.product?.stock_status === 'IN_STOCK' || !!cur.product?.presale_product?.status;
      const { attributes } = cur;

      if (!Array.isArray(attributes) || attributes.length === 0) {
        return acc;
      }

      const valueUids = attributes.map(({ uid }) => uid);

      for (let i = 0, len = attributes.length; i < len; i++) {
        for (const valueUidList of combine(valueUids, i + 1)) {
          const valueUidGroup = valueUidList.sort().join(',');

          if (isInStock) {
            acc[valueUidGroup] = 1;
            continue;
          }

          if (!Object.prototype.hasOwnProperty.call(acc, valueUidGroup)) {
            acc[valueUidGroup] = 0;
          }
        }
      }

      return acc;
    }, {});
  }, [variants]);

  const getOptionIsOutOfStock = (formFiled: string, valueUid: string) => {
    if (!configurableOptions) {
      return false;
    }

    const valueUidGroup = configurableOptions
      .reduce(
        (acc, cur) => {
          const optionFormFiled = cur.attribute_code;
          const selectedUid = allFormData?.[optionFormFiled]?.uid;

          if (optionFormFiled === formFiled || !selectedUid) {
            return acc;
          }

          acc.push(String(selectedUid));

          return acc;
        },
        [String(valueUid)],
      )
      .sort()
      .join(',');

    return !variantStockStatusMap[valueUidGroup];
  };

  if (!isProductConfigurable(product)) {
    return null;
  }

  // configurableOptions 排序
  const sortedConfigurableOptions = configurableOptions.sort((a, b) => a.position - b.position);

  const sortedColor = getSortedColor({
    values: sortedConfigurableOptions.find(item => item.attribute_code === 'lw_color')?.values as Value[],
    variants,
    attribute_code: 'lw_color',
  });

  return (
    <div className='flex flex-col gap-y-5 lg_gap-y-[30px]'>
      {sortedConfigurableOptions.map((option, clickIndex) => {
        if (option.attribute_code === 'lw_color') {
          return (
            <FormField
              key={option.uid}
              control={form.control}
              name={option.attribute_code}
              render={({ field }) => (
                <FormItem className='space-y-2.5 lg_space-y-5'>
                  <FormLabel>
                    <span className='font-roboto-medium text-[18px] font-medium leading-none'>{option.label}:</span>
                    <span className='ml-[5px] font-roboto-regular text-[18px]  leading-6'>
                      {allFormData?.['lw_color']?.label}
                    </span>
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={value => {
                        const selectedOption = option.values.find(v => v.uid.toString() === value);

                        if (selectedOption) {
                          field.onChange({
                            uid: value,
                            label: selectedOption?.label,
                            option_attribute_id: option.attribute_id,
                            option_selection_value: selectedOption?.value_index,
                          });

                          const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index.toString());

                          if (shouldAddToSpec) {
                            handleClick({
                              bool: true,
                              text: selectedOption?.value_index?.toString(),
                              index: clickIndex,
                            });
                          } else {
                            handleClick({
                              bool: true,
                              text: '',
                              index: clickIndex,
                            });
                          }

                          handleColorLabelChange(selectedOption?.label.toLowerCase());
                        }
                      }}
                      value={field.value?.uid}
                      className={clsx(
                        'mt-2.5 flex flex-wrap gap-x-[18px] gap-y-2.5 lg_mt-5 lg_gap-x-[25px]',
                        classes?.colorOptionsWrapper,
                      )}
                    >
                      {sortedColor.map(value => (
                        <ColorOptionRadioItem
                          key={value.uid}
                          item={value}
                          attributeCode={option.attribute_code}
                          variants={variants}
                          classes={classes}
                          isOutOfStock={getOptionIsOutOfStock(option.attribute_code, value.uid)}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage className='text-error font-roboto-regular text-sm font-normal italic leading-none text-[#e82f2f]' />
                </FormItem>
              )}
            />
          );
        }

        return (
          <FormField
            key={option.uid}
            control={form.control}
            name={option.attribute_code}
            render={({ field }) => (
              <FormItem className='space-y-2.5 lg_space-y-5'>
                <FormLabel>
                  <div>
                    <span className='font-roboto-medium text-[18px] font-medium leading-none'>{option.label}:</span>
                    <span className='ml-[5px] font-roboto-regular text-[18px]  leading-6'>
                      {allFormData?.[option.attribute_code]?.label}
                    </span>
                  </div>
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={value => {
                      const selectedOption = option.values.find(v => v.uid.toString() === value);

                      if (selectedOption) {
                        field.onChange({
                          uid: value,
                          label: selectedOption?.label,
                          option_attribute_id: option.attribute_id,
                          option_selection_value: selectedOption?.value_index,
                        });

                        const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index.toString());

                        if (shouldAddToSpec) {
                          handleClick({
                            bool: true,
                            text: selectedOption?.value_index?.toString(),
                            index: clickIndex,
                          });
                        } else {
                          handleClick({
                            bool: true,
                            text: '',
                            index: clickIndex,
                          });
                        }
                      }
                    }}
                    value={field.value?.uid}
                    className='flex flex-wrap gap-5 lg_gap-y-5'
                  >
                    {option.values.map(value => (
                      <ConfigurableOptionRadioItem
                        key={value.uid}
                        item={value}
                        isSize={option.label === 'Size'}
                        isOutOfStock={getOptionIsOutOfStock(option.attribute_code, value.uid)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage className='text-error font-roboto-regular text-sm font-normal italic leading-none text-[#e82f2f]' />
              </FormItem>
            )}
          />
        );
      })}
    </div>
  );
};

const ColorOptionRadioItem = ({
  item,
  attributeCode,
  variants,
  isOutOfStock,
  classes,
}: {
  item: Value;
  attributeCode: string;
  variants: any[];
  isOutOfStock: boolean;
  classes: any;
}) => {
  const form = useFormContext();
  const { watch } = form;
  const allFormData = watch();
  const { lw_toe_shape } = allFormData || {};
  const { option_selection_value: toe_selection } = lw_toe_shape || {};

  const optionImage = useMemo(() => {
    let find;

    if (toe_selection) {
      find = variants.find(temp => {
        const lw_color_attr = temp.attributes.find((attr: any) => attr.code === 'lw_color');

        const lw_toe_shape_attr = temp.attributes.find((attr: any) => attr.code === 'lw_toe_shape');

        return lw_color_attr?.value_index === item.value_index && lw_toe_shape_attr?.value_index === toe_selection;
      });
    } else {
      find = variants.find(temp => {
        return temp.attributes.some((attr: any) => attr.value_index === item.value_index);
      });
    }

    if (find) {
      const media_gallery_array = find.product.media_gallery_entries;

      // 默认展示图片position最小的
      let media = media_gallery_array[0];

      media_gallery_array.forEach((item: any, index: number) => {
        if (item.position < media.position) {
          media = item;
        }
      });

      return media?.url as string;
    }

    // return '/logo.jpg'
    return '';
  }, [toe_selection, variants, item.value_index]);

  if (!optionImage) {
    return null;
  }

  return (
    <FormItem className='flex items-center space-x-0 space-y-0'>
      <FormControl>
        <RadioGroupItem
          value={item.uid}
          className='peer sr-only'
        />
      </FormControl>
      {
        <FormLabel
          className={cn(
            'relative flex cursor-pointer flex-col items-center overflow-hidden rounded border-1 border-solid border-transparent',
            ' hover_border-primary peer-data-[state=checked]_border-primary ',
          )}
        >
          {attributeCode === 'lw_color' && optionImage && (
            <Image
              src={optionImage}
              alt={item.label}
              width={1265}
              height={1600}
              className={clsx('h-auto w-[55px] lg_w-[70px]', classes?.colorOptionImage, isOutOfStock && 'opacity-60')}
              loader={({ src }) => src}
              unoptimized
            />
          )}
          {isOutOfStock && <OutOfStockCrossLine />}
        </FormLabel>
      }
    </FormItem>
  );
};

const ConfigurableOptionRadioItem = ({
  item,
  isSize,
  isOutOfStock,
}: {
  item: Value;
  isSize: boolean;
  isOutOfStock: boolean;
}) => {
  return (
    <FormItem className='flex items-center space-x-0 space-y-0'>
      <FormControl>
        <RadioGroupItem
          value={item.uid}
          className='peer sr-only'
        />
      </FormControl>
      <FormLabel
        className={cn(
          'relative flex h-[54px] min-w-[54px] cursor-pointer items-center justify-center rounded bg-f9f9f9 px-1 font-roboto-regular text-[18px] leading-6   lg_h-[54px] lg_min-w-[54px] lg_text-base',
          'border-1 border-solid border-transparent peer-data-[state=checked]_border-black lg_hover_border-black [&:has([data-state=checked])]_border-black',
          isOutOfStock && 'text-[#929292]',
        )}
      >
        <span>{isSize ? item?.swatch_data?.value || item?.label : item?.label}</span>
        {isOutOfStock && <OutOfStockCrossLine />}
      </FormLabel>
    </FormItem>
  );
};

const OutOfStockCrossLine = () => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [lineWidth, setLineWidth] = useState(0);
  const [leftRotateDeg, setLeftRotateDeg] = useState(0);
  const { isMobile } = useIsMobile();

  useEffect(() => {
    const generateState = () => {
      const wrapper = wrapperRef.current;

      if (!wrapper) return;

      const { offsetWidth, offsetHeight } = wrapper;

      setLineWidth(Math.sqrt(offsetHeight * offsetHeight + offsetWidth * offsetWidth));
      setLeftRotateDeg((Math.atan(offsetHeight / offsetWidth) * 180) / Math.PI);
    };

    isMobile ? generateState() : generateState();
  }, [isMobile]);

  return (
    <div
      ref={wrapperRef}
      className={clsx(' absolute left-0 top-0 h-full w-full ')}
    >
      <div
        className={clsx(' absolute left-1/2 top-1/2 h-[1px] flex-none  bg-[#929292]')}
        style={{ width: lineWidth, transform: `translateX(-50%) translateY(-50%) rotateZ(${leftRotateDeg}deg)` }}
      />
      <div
        className={clsx(' absolute left-1/2 top-1/2 h-[1px] flex-none bg-[#929292]')}
        style={{ width: lineWidth, transform: `translateX(-50%) translateY(-50%) rotateZ(-${leftRotateDeg}deg)` }}
      />
    </div>
  );
};

const combine = (elements: (string | number)[], count: number) => {
  if (count > elements.length || count <= 0) {
    return [];
  }

  if (count === elements.length) {
    return [elements];
  }

  if (count === 1) {
    return elements.map(element => [element]);
  }

  const result = [];
  const stack: { start: number; comb: (string | number)[]; depth: number }[] = [
    {
      start: 0,
      comb: [],
      depth: 0,
    },
  ];
  const elementCount = elements.length;

  while (stack.length > 0) {
    const { start, comb, depth } = stack.pop()!;

    if (depth === count) {
      result.push(comb);
      continue;
    }

    for (let i = start; i <= elementCount - (count - depth); i++) {
      stack.push({
        start: i + 1,
        comb: comb.concat(elements[i]),
        depth: depth + 1,
      });
    }
  }

  return result;
};
